import type { Disposer } from '@elara/core/types';
import type { Cost } from '@elara/domain/models/schemas/shared-schemas';
import type { MessageRef,ShortlistItemLocation,ShortlistItemType } from '@elara/domain/models/schemas/shortlist-schemas';

import type { ShortlistContainer } from '#containers/ShortlistContainer.js';
import type { ShortlistGroup } from '#models/ShortlistGroupModel.js';
import type { ShortlistItem } from '#models/ShortlistItemModel.js';
import type { QueryHandle } from '../firebase/types.js';
import type { ToastState } from './ToastState.js';
import type { TripsState } from './TripsState.js';

import { effect } from '@preact/signals-react';

import { trackShortlistItemAdded, trackShortlistItemDeleted } from '#lib/analytics-events.js';
import { getErrorReporter } from '#lib/error-reporting.js';
import { EMPTY_QUERY } from '#lib/firebolt-utils.js';
import { metadataMatches } from '#lib/occupancy-utils.js';
import { createState } from '#lib/signals-utils.js';
import { metadataForIdentity } from '#lib/utils.js';

/**
 * Input data for adding a new shortlist item.
 * Flow accepts referenceId, type, groupId?, metadata?; title is optional (displayTitle used for display).
 */
export interface AddShortlistItemInput {
  type: ShortlistItemType;
  referenceId: string;
  title?: string;
  description?: string;
  referenceData?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  messageRef?: MessageRef;
  groupId?: string;
  cost?: Cost;
  images?: string[];
  location?: ShortlistItemLocation;
}

/**
 * The main reactive store for shortlist functionality.
 *
 * @remark All the getters in FlatStore are transformed into computed signals, the values are signals.
 * The functions are not touched, they are the actions on the state.
 */
export function createShortlistState(
  tripsState: TripsState,
  shortlist: ShortlistContainer,
  toastState: ToastState,
) {
  const { ClientShortlistItem, ClientShortlistGroup } = shortlist;

  const state = createState({
    /** Shared promise for creating the default group to avoid race conditions */
    defaultGroupPromise: undefined as Promise<string | undefined> | undefined,

    /** Whether a group is being created */
    isCreatingGroup: false,

    /** Optimistic updates for groupId changes - Record<itemId, { groupId: string, updatedAt: Date }> */
    optimisticGroupUpdates: {} as Record<string, { groupId: string; updatedAt: Date }>,

    /** Optimistic updates for position changes - Record<itemId, position> */
    optimisticPositionUpdates: {} as Record<string, number>,

    /** Optimistic updates for group name changes - Record<groupId, name> */
    optimisticGroupNameUpdates: {} as Record<string, string>,

    /** Optimistic updates for group position changes - Record<groupId, position> */
    optimisticGroupPositionUpdates: {} as Record<string, number>,

    /** Record of group IDs currently being renamed (for UI animation) - Record<groupId, true> */
    renamingGroupIds: {} as Record<string, true>,

    /** Track previous trip ID to detect trip changes for query cleanup */
    _previousTripId: undefined as string | undefined,
    /** Previous items query handle for cleanup on trip switch */
    _prevItemsQuery: undefined as QueryHandle<ShortlistItem> | undefined,
    /** Previous groups query handle for cleanup on trip switch */
    _prevGroupsQuery: undefined as QueryHandle<ShortlistGroup> | undefined,
    /** Collection of cleanup functions for all effects */
    cleanupFunctions: [] as Disposer[],
    /** Cleanup function reference to ensure idempotency */
    cleanupFunctionRef: null as Disposer | null,

    /** Whether to show totals in the shortlist */
    showTotals: false,

    /** Toggle showing totals in the shortlist */
    toggleShowTotals(): void {
      this.showTotals = !this.showTotals;
    },

    /** Get shortlist items query for the current trip */
    get itemsQuery(): QueryHandle<ShortlistItem> {
      const tripId = tripsState.current?.id;
      if (!tripId) return EMPTY_QUERY;
      return ClientShortlistItem.list({ tripId });
    },

    /** Get shortlist items for the current trip with optimistic updates applied */
    get items(): ShortlistItem[] {
      const baseItems = this.itemsQuery.items;
      const optimisticGroupUpdates = this.optimisticGroupUpdates;
      const optimisticPositionUpdates = this.optimisticPositionUpdates;
      
      // If no optimistic updates, return items as-is
      if (Object.keys(optimisticGroupUpdates).length === 0 && Object.keys(optimisticPositionUpdates).length === 0) {
        return baseItems;
      }

      // Apply optimistic updates to items
      const itemsWithUpdates = baseItems.map((item) => {
        const optimisticGroupUpdate = optimisticGroupUpdates[item.id];
        const optimisticPosition = optimisticPositionUpdates[item.id];
        const updates: Partial<ShortlistItem> = {};
        
        if (optimisticGroupUpdate) {
          updates.groupId = optimisticGroupUpdate.groupId;
          updates.updatedAt = optimisticGroupUpdate.updatedAt;
        }
        
        if (optimisticPosition !== undefined) {
          updates.position = optimisticPosition;
        }
        
        if (Object.keys(updates).length > 0) {
          return { ...item, ...updates };
        }
        return item;
      });

      // Sort items by position (ascending)
      return itemsWithUpdates.toSorted((a, b) => {
        return a.position - b.position;
      });
    },

    /** Whether shortlist items and groups are ready */
    get isReady(): boolean {
      return this.itemsQuery.isReady && this.groupsQuery.isReady;
    },

    /** Get shortlist groups query for the current trip */
    get groupsQuery(): QueryHandle<ShortlistGroup> {
      const tripId = tripsState.current?.id;
      if (!tripId) return EMPTY_QUERY;
      return ClientShortlistGroup.list({ tripId });
    },

    /** Get shortlist groups for the current trip with optimistic name and position updates applied, sorted by position */
    get groups(): ShortlistGroup[] {
      const baseGroups = this.groupsQuery.items;
      const optimisticNameUpdates = this.optimisticGroupNameUpdates;
      const optimisticPositionUpdates = this.optimisticGroupPositionUpdates;
      
      // If no optimistic updates, return groups sorted by position
      if (Object.keys(optimisticNameUpdates).length === 0 && Object.keys(optimisticPositionUpdates).length === 0) {
        return baseGroups.toSorted((a, b) => {
          const posA = a.position ?? 0;
          const posB = b.position ?? 0;
          return posA - posB;
        });
      }

      // Apply optimistic updates to groups
      const groupsWithUpdates = baseGroups.map((group) => {
        const optimisticName = optimisticNameUpdates[group.id];
        const optimisticPosition = optimisticPositionUpdates[group.id];
        const updates: Partial<ShortlistGroup> = {};
        
        if (optimisticName !== undefined) {
          updates.name = optimisticName;
        }
        
        if (optimisticPosition !== undefined) {
          updates.position = optimisticPosition;
        }
        
        if (Object.keys(updates).length > 0) {
          return { ...group, ...updates };
        }
        return group;
      });

      // Sort by position (ascending)
      return groupsWithUpdates.toSorted((a, b) => {
        const posA = a.position ?? 0;
        const posB = b.position ?? 0;
        return posA - posB;
      });
    },

    /** Get items grouped by groupId, sorted by position */
    get itemsByGroup(): Record<string, ShortlistItem[]> {
      const grouped: Record<string, ShortlistItem[]> = {};

      // Initialize groups
      for (const group of this.groups) {
        grouped[group.id] = [];
      }

      // Distribute items (only items with valid groupIds)
      for (const item of this.items) {
        if (item.groupId && grouped[item.groupId]) {
          grouped[item.groupId]!.push(item);
        }
        // Items without groupId are ignored (should not exist)
      }

      // Sort items within each group by position (items are already sorted, but ensure consistency)
      for (const groupId in grouped) {
        grouped[groupId] = grouped[groupId]!.toSorted((a, b) => a.position - b.position);
      }

      return grouped;
    },

    /** Get items by type */
    get itemsByType(): Record<ShortlistItemType, ShortlistItem[]> {
      const byType: Record<ShortlistItemType, ShortlistItem[]> = {
        hotel: [],
        room: [],
        flight: [],
        destination: [],
        activity: [],
        restaurant: [],
        experience: [],
        place: [],
      };

      for (const item of this.items) {
        byType[item.type].push(item);
      }

      return byType;
    },

    /** Total count of shortlist items */
    get totalCount(): number {
      return this.items.length;
    },

    /**
     * Whether the shortlist has nothing the traveler put there.
     *
     * `CreateTripFlow` seeds every trip with a single empty "My Favorites" group, so a lone empty
     * group is an artefact of trip creation rather than something the traveler made. Screens use
     * this to choose between the empty state and the group list: an empty card grid reads as a
     * broken page (ELARA-1000). Two or more groups means deliberate curation, so they stay visible
     * even while still unfilled.
     */
    get isEmpty(): boolean {
      return this.totalCount === 0 && this.groups.length <= 1;
    },

    /**
     * Check if an item is shortlisted by referenceId and type.
     * When metadata is provided, matches only if every key in metadata equals item.metadata?.[key].
     * Omit metadata for "any entry for this referenceId".
     */
    isShortlisted(type: ShortlistItemType, referenceId: string, metadata?: Record<string, unknown>): boolean {
      return this.items.some((item) => {
        if (item.type !== type || item.referenceId !== referenceId) return false;
        if (metadata === undefined) return true;
        return metadataMatches(item.metadata, metadata);
      });
    },

    /** Get shortlist item by referenceId and type; optional metadata for partial match (identity keys from `metadataForIdentity`). */
    getByReference(type: ShortlistItemType, referenceId: string, metadata?: Record<string, unknown>): ShortlistItem | undefined {
      return this.items.find((item) => {
        if (item.type !== type || item.referenceId !== referenceId) return false;
        if (metadata === undefined) return true;
        return metadataMatches(item.metadata, metadata);
      });
    },

    /** Get all shortlist items by referenceId and type (for items in multiple groups); optional metadata for partial match. */
    getAllByReference(type: ShortlistItemType, referenceId: string, metadata?: Record<string, unknown>): ShortlistItem[] {
      return this.items.filter((item) => {
        if (item.type !== type || item.referenceId !== referenceId) return false;
        if (metadata === undefined) return true;
        return metadataMatches(item.metadata, metadata);
      });
    },

    /** Get all shortlist entries for a room option (type room, referenceId = roomOptionId). For widget badge etc. */
    getShortlistEntriesForRoomOption(roomOptionId: string): ShortlistItem[] {
      return this.items.filter((item) => item.type === 'room' && item.referenceId === roomOptionId);
    },

    /** Add a new shortlist item */
    async addItem(input: AddShortlistItemInput): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) {
        toastState.addToast({
          title: 'Cannot add to shortlist',
          description: 'No trip selected',
          variant: 'destructive',
        });
        return;
      }

      try {
        // If no groups exist, create a default group first
        // This is a fallback for when the initial shortlist group is not created by the CreateTripFlow.
        let groupId = input.groupId;
        if (this.groups.length === 0) {
          // Avoid creating multiple "first" groups by sharing a single promise.
          if (!this.defaultGroupPromise) {
            this.defaultGroupPromise = this.createGroupSilent('My Favorites').finally(() => {
              this.defaultGroupPromise = undefined;
            });
          }

          const newGroupId = await this.defaultGroupPromise;
          if (newGroupId) {
            groupId = newGroupId;
          } else {
            // If group creation failed, we can't proceed without a group
            throw new Error('Failed to create default shortlist group');
          }
        } else if (!groupId) {
          // If no group specified, use the first group
          groupId = this.groups[0]?.id;
        }

        // Ensure we have a groupId (should never be undefined at this point)
        if (!groupId) {
          throw new Error('No group available for shortlist item');
        }

        await shortlist.addShortlistItem({
          tripId,
          referenceId: input.referenceId,
          type: input.type,
          groupId,
          ...(input.metadata != null && Object.keys(input.metadata).length > 0 && { metadata: input.metadata }),
        });
        trackShortlistItemAdded({ tripId, itemType: input.type });
        tripsState.pulseNavIcon('shortlist');
        toastState.addToast({
          title: 'Added to shortlist',
          variant: 'success',
          description: input.title ? `${input.title} has been saved to your shortlist` : 'Item has been saved to your shortlist',
        });
      } catch (error) {
        console.error('[ShortlistState] Failed to add item', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to add to shortlist',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },

    /** Add an item to each selected group. A group that already holds it is left as is. */
    async addItemToGroups(input: AddShortlistItemInput, groupIds: string[]): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) {
        toastState.addToast({
          title: 'Cannot add to shortlist',
          description: 'No trip selected',
          variant: 'destructive',
        });
        return;
      }

      if (groupIds.length === 0) {
        return;
      }

      try {
        for (const groupId of groupIds) {
          await shortlist.addShortlistItem({
            tripId,
            referenceId: input.referenceId,
            type: input.type,
            groupId,
            ...(input.metadata != null && Object.keys(input.metadata).length > 0 && { metadata: input.metadata }),
          });
        }

        tripsState.pulseNavIcon('shortlist');

        const groupNames = groupIds
          .map((id) => this.groups.find((g) => g.id === id)?.displayName)
          .filter(Boolean)
          .join(', ');

        toastState.addToast({
          title: 'Added to shortlist',
          variant: 'success',
          description:
            groupIds.length === 1
              ? (input.title ? `${input.title} has been saved to ${groupNames}` : `Item has been saved to ${groupNames}`)
              : (input.title ? `${input.title} has been saved to ${groupIds.length} groups` : `Item has been saved to ${groupIds.length} groups`),
        });
      } catch (error) {
        console.error('[ShortlistState] Failed to add item to groups', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to add to shortlist',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },

    /** Remove a shortlist item */
    async removeItem(itemId: string): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) return;

      // Capture the type before removal — the item leaves `items` once the delete lands.
      const itemType = this.items.find((i) => i.id === itemId)?.type;

      try {
        await shortlist.removeShortlistItem({ tripId, itemId });
        trackShortlistItemDeleted({ tripId, itemType });
        toastState.addToast({
          title: 'Removed from shortlist',
          variant: 'success',
          description: 'Item has been removed from your shortlist',
        });
      } catch (error) {
        console.error('[ShortlistState] Failed to remove item', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to remove from shortlist',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },

    /** Remove items from specific groups */
    async removeItemsFromGroups(itemIds: string[]): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) return;

      if (itemIds.length === 0) return;

      try {
        for (const itemId of itemIds) {
          await shortlist.removeShortlistItem({ tripId, itemId });
        }

        const groupNames = itemIds
          .map((id) => {
            const item = this.items.find((i) => i.id === id);
            if (!item?.groupId) return null;
            return this.groups.find((g) => g.id === item.groupId)?.displayName;
          })
          .filter(Boolean)
          .join(', ');

        toastState.addToast({
          title: 'Removed from shortlist',
          variant: 'success',
          description:
            itemIds.length === 1
              ? `Item has been removed from ${groupNames || 'shortlist'}`
              : `${itemIds.length} items have been removed from shortlist`,
        });
      } catch (error) {
        console.error('[ShortlistState] Failed to remove items from groups', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to remove from shortlist',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },

    /**
     * Toggle shortlist status for an item. Matches on the identity keys of `input.metadata`
     * (`metadataForIdentity`): a room is saved whole, so it matches by referenceId whatever its party.
     */
    async toggleItem(type: ShortlistItemType, referenceId: string, input: Omit<AddShortlistItemInput, 'type' | 'referenceId'>): Promise<void> {
      const identityMeta = metadataForIdentity(type, input.metadata);
      const existing = this.getByReference(type, referenceId, identityMeta);
      if (existing) {
        await this.removeItem(existing.id);
      } else {
        await this.addItem({ ...input, type, referenceId });
      }
    },

    /** Update a shortlist item */
    async updateItem(itemId: string, updates: Partial<AddShortlistItemInput>): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) return;

      try {
        await shortlist.updateShortlistItem({ tripId, itemId, updates });
      } catch (error) {
        console.error('[ShortlistState] Failed to update item', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to update shortlist item',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },

    /** Move an item to a group with optimistic updates */
    async moveToGroup(itemId: string, groupId: string, targetPosition?: number): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) return;

      // Find the current item to get its current groupId for rollback
      const currentItem = this.itemsQuery.items.find((i) => i.id === itemId);
      if (!currentItem) return;

      const previousGroupId = currentItem.groupId;

      // Don't do anything if already in the target group and no position change
      if (previousGroupId === groupId && targetPosition === undefined) return;

      // Calculate target position if not provided
      let finalPosition = targetPosition;
      if (finalPosition === undefined) {
        // Append to end: find max position in target group
        const targetGroupItems = this.itemsByGroup[groupId] || [];
        if (targetGroupItems.length === 0) {
          finalPosition = 0;
        } else {
          const maxPosition = Math.max(...targetGroupItems.map((i) => i.position));
          finalPosition = maxPosition + 1;
        }
      }

      // If moving to a specific position in a different group, we need to shift other items
      if (previousGroupId !== groupId && targetPosition !== undefined) {
        // Use reorderItems to handle the move and position shift
        const targetGroupItems = this.itemsByGroup[groupId] || [];
        const itemIds = targetGroupItems.map((i) => i.id);
        // Insert at target position
        itemIds.splice(targetPosition, 0, itemId);
        await this.reorderItems(groupId, itemIds);
        return;
      }

      // Apply optimistic updates immediately
      this.optimisticPositionUpdates = {
        ...this.optimisticPositionUpdates,
        [itemId]: finalPosition,
      };

      try {
        // Update in Firebase in the background
        const updates: { groupId: string; position?: number } = { groupId , position: finalPosition,};

        await shortlist.updateShortlistItem({ tripId, itemId, updates });
        
        // Clear optimistic updates - Firestore listener will update with real data shortly
        setTimeout(() => {
          const groupUpdates = { ...this.optimisticGroupUpdates };
          delete groupUpdates[itemId];
          this.optimisticGroupUpdates = groupUpdates;
          
          const positionUpdates = { ...this.optimisticPositionUpdates };
          delete positionUpdates[itemId];
          this.optimisticPositionUpdates = positionUpdates;
        }, 200);
      } catch (error) {
        // Rollback optimistic updates on error
        const groupUpdates = { ...this.optimisticGroupUpdates };
        delete groupUpdates[itemId];
        this.optimisticGroupUpdates = groupUpdates;
        
        const positionUpdates = { ...this.optimisticPositionUpdates };
        delete positionUpdates[itemId];
        this.optimisticPositionUpdates = positionUpdates;
        
        console.error('[ShortlistState] Failed to move item to group', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to move item',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },

    /** Reorder items within a group */
    async reorderItems(groupId: string, itemIds: string[]): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) return;

      if (itemIds.length === 0) return;

      // Calculate new positions based on itemIds array order (0, 1, 2, ...)
      const positionMap = new Map<string, number>();
      for (let index = 0; index < itemIds.length; index++) {
        const itemId = itemIds[index];
        if (itemId) {
          positionMap.set(itemId, index);
        }
      }

      // Apply optimistic position updates immediately
      const newOptimisticUpdates = { ...this.optimisticPositionUpdates };
      for (let index = 0; index < itemIds.length; index++) {
        const itemId = itemIds[index];
        if (itemId) {
          newOptimisticUpdates[itemId] = index;
        }
      }
      this.optimisticPositionUpdates = newOptimisticUpdates;

      try {
        // Prepare updates for batch reorder flow
        const updates = itemIds.map((itemId, index) => ({
          itemId,
          groupId,
          position: index,
        }));

        // Call batch reorder flow
        await shortlist.reorderItems({ tripId, updates });

        // Clear optimistic updates - Firestore listener will update with real data shortly
        setTimeout(() => {
          const positionUpdates = { ...this.optimisticPositionUpdates };
          for (const itemId of itemIds) {
            delete positionUpdates[itemId];
          }
          this.optimisticPositionUpdates = positionUpdates;
        }, 200);
      } catch (error) {
        // Rollback optimistic updates on error
        const positionUpdates = { ...this.optimisticPositionUpdates };
        for (const itemId of itemIds) {
          delete positionUpdates[itemId];
        }
        this.optimisticPositionUpdates = positionUpdates;

        console.error('[ShortlistState] Failed to reorder items', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to reorder items',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },

    /** Move an item to a specific position in a different group, updating positions of affected items */
    async moveToPosition(itemId: string, targetGroupId: string, targetPosition: number): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) return;

      const item = this.items.find((i) => i.id === itemId);
      if (!item) return;

      const targetGroupItems = this.itemsByGroup[targetGroupId] || [];
      
      // Create new array with item inserted at target position
      const newItemIds = [...targetGroupItems.map((i) => i.id)];
      newItemIds.splice(targetPosition, 0, itemId);

      // Prepare updates: moved item gets new groupId, all items in target group get new positions
      const updates = newItemIds.map((id, index) => {
        const existingItem = this.items.find((i) => i.id === id);
        return {
          itemId: id,
          groupId: id === itemId ? targetGroupId : (existingItem?.groupId || targetGroupId),
          position: index,
        };
      });

      // Apply optimistic updates
      const now = new Date();
      this.optimisticGroupUpdates = {
        ...this.optimisticGroupUpdates,
        [itemId]: { groupId: targetGroupId, updatedAt: now },
      };
      const newPositionUpdates: Record<string, number> = {};
      for (const update of updates) {
        newPositionUpdates[update.itemId] = update.position;
      }
      this.optimisticPositionUpdates = {
        ...this.optimisticPositionUpdates,
        ...newPositionUpdates,
      };

      try {
        // Call batch reorder flow directly
        await shortlist.reorderItems({ tripId, updates });

        // Clear optimistic updates - Firestore listener will update with real data shortly
        setTimeout(() => {
          const groupUpdates = { ...this.optimisticGroupUpdates };
          delete groupUpdates[itemId];
          this.optimisticGroupUpdates = groupUpdates;

          const positionUpdates = { ...this.optimisticPositionUpdates };
          for (const update of updates) {
            delete positionUpdates[update.itemId];
          }
          this.optimisticPositionUpdates = positionUpdates;
        }, 200);
      } catch (error) {
        // Rollback optimistic updates on error
        const groupUpdates = { ...this.optimisticGroupUpdates };
        delete groupUpdates[itemId];
        this.optimisticGroupUpdates = groupUpdates;

        const positionUpdates = { ...this.optimisticPositionUpdates };
        for (const update of updates) {
          delete positionUpdates[update.itemId];
        }
        this.optimisticPositionUpdates = positionUpdates;

        console.error('[ShortlistState] Failed to move item to position', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to move item',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },

    /** Copy an item to a group (creates a duplicate in the target group) */
    async copyToGroup(itemId: string, groupId: string): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) return;

      const item = this.items.find((i) => i.id === itemId);
      if (!item) {
        toastState.addToast({
          title: 'Item not found',
          description: 'The item could not be found',
          variant: 'destructive',
        });
        return;
      }

      const referenceId = item.referenceId ?? '';
      if (!referenceId) {
        toastState.addToast({
          title: 'Cannot copy',
          description: 'This item has no reference and cannot be copied',
          variant: 'destructive',
        });
        return;
      }

      try {
        await shortlist.addShortlistItem({
          tripId,
          referenceId,
          type: item.type,
          groupId,
          ...(item.metadata != null && Object.keys(item.metadata).length > 0 && { metadata: item.metadata }),
        });
        const targetGroup = this.groups.find((g) => g.id === groupId);
        const displayName = 'displayTitle' in item && typeof item.displayTitle === 'string' ? item.displayTitle : item.title ?? 'Item';
        toastState.addToast({
          title: 'Copied to group',
          description: `${displayName} has been copied to ${targetGroup?.displayName ?? 'the group'}`,
        });
      } catch (error) {
        console.error('[ShortlistState] Failed to copy item to group', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to copy item',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },

    /** Create a new group silently (no toast) - used for auto-creating default group */
    async createGroupSilent(name: string): Promise<string | undefined> {
      const tripId = tripsState.current?.id;
      if (!tripId) return;

      try {
        const result = await shortlist.createShortlistGroup({ tripId, group: { name } });
        return result.data?.id;
      } catch (error) {
        console.error('[ShortlistState] Failed to create group silently', error);
        getErrorReporter().captureException(error);
        return;
      }
    },

    /** Create a new group */
    async createGroup(name: string): Promise<string | undefined> {
      const tripId = tripsState.current?.id;
      if (!tripId) {
        toastState.addToast({
          title: 'Cannot create shortlist',
          description: 'No trip selected',
          variant: 'destructive',
        });
        return;
      }

      this.isCreatingGroup = true;
      try {
        const result = await shortlist.createShortlistGroup({ tripId, group: { name } });
        toastState.addToast({
          title: 'Shortlist created',
          description: `"${name}" has been created`,
        });
        return result.data?.id;
      } catch (error) {
        console.error('[ShortlistState] Failed to create shortlist', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to create shortlist',
          description: 'Please try again',
          variant: 'destructive',
        });
        return;
      } finally {
        this.isCreatingGroup = false;
      }
    },

    /** Rename a group with optimistic updates */
    async renameGroup(groupId: string, name: string): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) return;

      // Find the current group to get its current name for rollback
      const currentGroup = this.groupsQuery.items.find((g) => g.id === groupId);
      if (!currentGroup) return;

      const previousName = currentGroup.name;

      // Don't do anything if name hasn't changed
      if (previousName === name) return;

      // Apply optimistic update immediately
      this.optimisticGroupNameUpdates = {
        ...this.optimisticGroupNameUpdates,
        [groupId]: name,
      };

      // Track that this group is being renamed (for UI animation)
      this.renamingGroupIds = { ...this.renamingGroupIds, [groupId]: true };

      try {
        // Update in Firebase in the background
        await shortlist.updateShortlistGroup({ tripId, groupId, updates: { name } });
        
        // Clear optimistic update - Firestore listener will update with real data shortly
        // We clear it after a brief delay to ensure smooth transition
        setTimeout(() => {
          const updates = { ...this.optimisticGroupNameUpdates };
          delete updates[groupId];
          this.optimisticGroupNameUpdates = updates;

          // Clear renaming state
          const renaming = { ...this.renamingGroupIds };
          delete renaming[groupId];
          this.renamingGroupIds = renaming;
        }, 200);
      } catch (error) {
        // Rollback optimistic update on error
        const updates = { ...this.optimisticGroupNameUpdates };
        delete updates[groupId];
        this.optimisticGroupNameUpdates = updates;

        // Clear renaming state
        const renaming = { ...this.renamingGroupIds };
        delete renaming[groupId];
        this.renamingGroupIds = renaming;
        
        console.error('[ShortlistState] Failed to rename shortlist', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to rename shortlist',
          description: 'Please try again',
          variant: 'destructive',
        });
        
        // Re-throw error so UI can handle it (e.g., switch back to edit mode)
        throw error;
      }
    },

    /** Delete a group and all items in it */
    async deleteGroup(groupId: string): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) return;

      const group = this.groups.find((g) => g.id === groupId);
      const itemsInGroup = this.itemsByGroup[groupId] || [];
      const itemCount = itemsInGroup.length;

      try {
        await shortlist.deleteShortlistGroup({ tripId, groupId });
        toastState.addToast({
          title: 'Group deleted',
          description:
            itemCount > 0
              ? `"${group?.displayName || 'Shortlist'}" and ${itemCount} item${itemCount === 1 ? '' : 's'} deleted`
              : `"${group?.displayName || 'Shortlist'}" deleted`,
        });
      } catch (error) {
        console.error('[ShortlistState] Failed to delete shortlist', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to delete shortlist',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },

    /** Reorder groups within a trip */
    async reorderGroups(groupIds: string[]): Promise<void> {
      const tripId = tripsState.current?.id;
      if (!tripId) return;

      if (groupIds.length === 0) return;

      // Calculate new positions based on groupIds array order (0, 1, 2, ...)
      const newOptimisticUpdates: Record<string, number> = {};
      for (let index = 0; index < groupIds.length; index++) {
        const groupId = groupIds[index];
        if (groupId) {
          newOptimisticUpdates[groupId] = index;
        }
      }

      // Apply optimistic position updates immediately
      this.optimisticGroupPositionUpdates = {
        ...this.optimisticGroupPositionUpdates,
        ...newOptimisticUpdates,
      };

      try {
        // Prepare updates for batch reorder flow
        const updates = groupIds.map((groupId, index) => ({
          groupId,
          position: index,
        }));

        // Call batch reorder flow
        await shortlist.reorderGroups({ tripId, updates });

        // Clear optimistic updates - Firestore listener will update with real data shortly
        setTimeout(() => {
          const positionUpdates = { ...this.optimisticGroupPositionUpdates };
          for (const groupId of groupIds) {
            delete positionUpdates[groupId];
          }
          this.optimisticGroupPositionUpdates = positionUpdates;
        }, 200);
      } catch (error) {
        // Rollback optimistic updates on error
        const positionUpdates = { ...this.optimisticGroupPositionUpdates };
        for (const groupId of groupIds) {
          delete positionUpdates[groupId];
        }
        this.optimisticGroupPositionUpdates = positionUpdates;

        console.error('[ShortlistState] Failed to reorder groups', error);
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to reorder groups',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },
    /** Start monitoring trip changes to clean up stale Firestore listeners (idempotent) */
    startMonitors(): Disposer {
      if (this.cleanupFunctionRef) return this.cleanupFunctionRef;

      const effects = [
        // Unsubscribe old shortlist queries when the trip changes
        effect(() => {
          const currentTripId = tripsState.current?.id;
          if (this._previousTripId !== undefined && this._previousTripId !== currentTripId) {
            if (this._prevItemsQuery && this._prevItemsQuery !== EMPTY_QUERY) {
              this._prevItemsQuery.unsubscribe();
            }
            if (this._prevGroupsQuery && this._prevGroupsQuery !== EMPTY_QUERY) {
              this._prevGroupsQuery.unsubscribe();
            }
          }
          this._prevItemsQuery = currentTripId ? this.itemsQuery : undefined;
          this._prevGroupsQuery = currentTripId ? this.groupsQuery : undefined;
          this._previousTripId = currentTripId;
        }),
      ];

      this.cleanupFunctions.push(...effects);
      this.cleanupFunctionRef = () => this.stopMonitors();
      return this.cleanupFunctionRef;
    },

    /** Clean up all effects to prevent memory leaks */
    stopMonitors(): void {
      // Unsubscribe any live queries before stopping
      if (this._prevItemsQuery && this._prevItemsQuery !== EMPTY_QUERY) {
        this._prevItemsQuery.unsubscribe();
        this._prevItemsQuery = undefined;
      }
      if (this._prevGroupsQuery && this._prevGroupsQuery !== EMPTY_QUERY) {
        this._prevGroupsQuery.unsubscribe();
        this._prevGroupsQuery = undefined;
      }
      for (const cleanup of this.cleanupFunctions) cleanup();
      this.cleanupFunctions.length = 0;
      this.cleanupFunctionRef = null;
    },
  });

  return state;
}

export type ShortlistState = ReturnType<typeof createShortlistState>;

